import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "components/basic/button";
import { headerOffset } from "components/basic/header";
import { DEFAULT_PROFILE_TAB_HEIGHT } from "components/basic/tabs";
import { DashboardProfileData } from "../..";
import { useVendorFieldSave } from "../../helpers/useVendorFieldSave";
import InsuranceSection from "./components/InsuranceSection";
import InstallationSection from "./components/InstallationSection";

interface DocumentsProfileDashboardProps {
  /** Where the profile tabs live: "profile", or "application" before approval. */
  basePath?: string;
  dashboardProfileData: DashboardProfileData;
  onVendorChange: (name: string, value: string | boolean) => void;
}

/** The documents that keep a vendor listed, and their expiry dates. */
const DocumentsProfileDashboard: React.FC<DocumentsProfileDashboardProps> = ({
  basePath = "profile",
  dashboardProfileData,
  onVendorChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("profile");
  const navigate = useNavigate();
  const saveVendor = useVendorFieldSave();
  const { vendor } = dashboardProfileData;

  const handleSave = async (updates: Record<string, string | null>) => {
    const saved = await saveVendor(updates);
    if (saved) {
      Object.entries(updates).forEach(([name, value]) =>
        onVendorChange(name, value ?? ""),
      );
    }
    return saved;
  };

  if (!vendor) {
    return null;
  }

  const buttonStyle: React.CSSProperties = {
    color: theme.palette.common.white,
    fontSize: "1.2em",
    fontWeight: "bold",
  };

  return (
    <div
      style={{
        display: "flex",
        overflow: "auto",
        scrollbarWidth: "thin",
        width: "100%",
        height: `calc(100vh - ${headerOffset(DEFAULT_PROFILE_TAB_HEIGHT + 1)})`,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          padding: theme.spacing(1.8),
          // Stretches to the parent's fixed viewport height, so the padding
          // has to sit inside it or it forces a scrollbar.
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "560px",
            overflowY: "auto",
            scrollbarWidth: "thin",
          }}
        >
          <InsuranceSection vendor={vendor} onSave={handleSave} />
          <InstallationSection vendor={vendor} onSave={handleSave} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            paddingTop: theme.spacing(2),
            gap: theme.spacing(2),
          }}
        >
          <Button
            style={buttonStyle}
            // Two 80px-padded buttons overflow a phone-width row.
            sx={{ paddingX: { xs: 4, sm: 10 }, paddingY: 0 }}
            variant="contained"
            color="primary"
            onClick={() => navigate(`${basePath}/business`)}
            size="large"
          >
            {t("back")}
          </Button>

          {/* An application has no Brand Identity tab to move on to. */}
          {basePath === "profile" && (
            <Button
              style={buttonStyle}
              sx={{ paddingX: { xs: 4, sm: 10 }, paddingY: 0 }}
              variant="contained"
              color="warning"
              onClick={() => navigate("profile/brand_identity")}
              size="large"
            >
              {t("next")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsProfileDashboard;
